import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";

const DEFAULT_ALLOWED_ROLES = ["1", "2", "6", "8"];

const normalizeEmail = (value = "") => String(value || "").trim().toLowerCase();

const getVolunteerEmail = (voluntario) =>
  voluntario?.email || voluntario?.correo || voluntario?.usuario || voluntario?.mail || "";

const getStoredRole = () => {
  try {
    return String(JSON.parse(localStorage.getItem("token") || "{}")?.rol || "");
  } catch (error) {
    return "";
  }
};

const getStoredToken = () => {
  try {
    return JSON.parse(localStorage.getItem("token") || "{}")?.idToken || "";
  } catch (error) {
    return "";
  }
};

const formatActivity = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || "Convocatoria seleccionada";

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "Convocatoria seleccionada";

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.filter(Boolean).join(", ") || "Convocatoria seleccionada";
    } catch (error) {
      return trimmed;
    }

    return trimmed;
  }

  return value || "Convocatoria seleccionada";
};

const getResultCount = (value) => {
  if (Array.isArray(value)) return value.length;
  return Number(value) || 0;
};

const normalizeVolunteer = (usuario) => ({
  ...usuario,
  email: getVolunteerEmail(usuario),
  nombreCompleto:
    usuario.nombreCompleto ||
    [usuario.nombres, usuario.apellidos].filter(Boolean).join(" ") ||
    usuario.nombre ||
    getVolunteerEmail(usuario),
});

const AgregarParticipantesConvocatoriaModal = ({
  open,
  onClose,
  convocatoria,
  onSuccess,
  allowedRoles = DEFAULT_ALLOWED_ROLES,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const volunteerRequestRef = useRef(null);
  const [voluntariosPool, setVoluntariosPool] = useState([]);
  const [voluntariosLoaded, setVoluntariosLoaded] = useState(false);
  const [loadingVoluntarios, setLoadingVoluntarios] = useState(false);
  const [participantesSeleccionados, setParticipantesSeleccionados] = useState([]);
  const [inputParticipante, setInputParticipante] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const currentRole = getStoredRole();
  const canManage = allowedRoles.map(String).includes(currentRole);
  const idConvocatoria = convocatoria?.idConvocatoria ?? convocatoria?.id;

  const convocatoriaTitle = useMemo(
    () =>
      formatActivity(
        convocatoria?.actividad ||
          convocatoria?.descripcionConvo ||
          convocatoria?.descripcion ||
          convocatoria?.nombreJornada
      ),
    [convocatoria]
  );

  const loadVoluntarios = useCallback(async () => {
    if (voluntariosLoaded || volunteerRequestRef.current) return;

    setLoadingVoluntarios(true);
    setError("");

    const request = axios.get("/usuariostranscripcion");
    volunteerRequestRef.current = request;

    try {
      const response = await request;
      const datos = Array.isArray(response.data) ? response.data : [];
      setVoluntariosPool(datos.map(normalizeVolunteer));
      setVoluntariosLoaded(true);
    } catch (requestError) {
      setError("No se pudo cargar el listado de voluntarios registrados.");
    } finally {
      volunteerRequestRef.current = null;
      setLoadingVoluntarios(false);
    }
  }, [voluntariosLoaded]);

  useEffect(() => {
    if (!open) return;

    setParticipantesSeleccionados([]);
    setInputParticipante("");
    setError(canManage ? "" : "No tiene permiso para agregar participantes a esta convocatoria.");
    setSuccess("");
  }, [canManage, convocatoria, open]);

  useEffect(() => {
    if (open && canManage) {
      loadVoluntarios();
    }
  }, [canManage, loadVoluntarios, open]);

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSelectionChange = (event, newValue) => {
    const seen = new Set();
    const unique = newValue.filter((voluntario) => {
      const email = normalizeEmail(getVolunteerEmail(voluntario));
      if (!email || seen.has(email)) return false;
      seen.add(email);
      return true;
    });

    setParticipantesSeleccionados(unique);
    setError("");
  };

  const handleRemoveVolunteer = (voluntario) => {
    const email = normalizeEmail(getVolunteerEmail(voluntario));
    setParticipantesSeleccionados((current) =>
      current.filter((item) => normalizeEmail(getVolunteerEmail(item)) !== email)
    );
  };

  const handleSave = async () => {
    if (!canManage) {
      setError("No tiene permiso para agregar participantes a esta convocatoria.");
      return;
    }

    if (!idConvocatoria) {
      setError("No se encontró la convocatoria seleccionada.");
      return;
    }

    const emails = Array.from(
      new Set(
        participantesSeleccionados
          .map((voluntario) => normalizeEmail(getVolunteerEmail(voluntario)))
          .filter(Boolean)
      )
    );

    if (!emails.length) {
      setError("Debe seleccionar al menos un voluntario registrado con email.");
      return;
    }

    const token = getStoredToken();

    if (!token) {
      setError("No se encontró una sesión válida. Inicie sesión nuevamente.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post("/convocatoria/agregar-participantes", {
        idConvocatoria,
        voluntarios: emails.map((email) => ({ email })),
      });

      const agregados = getResultCount(response.data?.agregados ?? response.data?.insertados);
      const ignorados = getResultCount(response.data?.ignorados ?? response.data?.duplicados);
      const errores = getResultCount(response.data?.errores);
      const resumen = [
        "Participantes agregados en espera de confirmación.",
        agregados > 0 ? `Agregados: ${agregados}.` : "",
        ignorados > 0 ? `Ignorados: ${ignorados}.` : "",
        errores > 0 ? `Errores: ${errores}.` : "",
      ]
        .filter(Boolean)
        .join(" ");

      setSuccess(resumen);
      setParticipantesSeleccionados([]);
      setInputParticipante("");
      await onSuccess?.(response.data);
    } catch (requestError) {
      const status = requestError?.response?.status;
      const backendMessage = requestError?.response?.data?.message || requestError?.response?.data?.error;

      if (status === 400) {
        setError(backendMessage || "Revise los participantes seleccionados.");
      } else if (status === 401) {
        setError(backendMessage || "No fue posible autenticar la solicitud. Verifique la sesión.");
      } else if (status === 403) {
        setError("No tiene permiso para agregar participantes a esta convocatoria.");
      } else {
        setError(backendMessage || "No se pudieron agregar los participantes.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-agregar-participante"
      aria-describedby="modal-agregar-participante-description"
      slotProps={{ backdrop: { style: { opacity: 0.2 } } }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isSmallScreen ? "92%" : "min(760px, 80vw)",
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: theme.palette.background.alt,
          border: "2px solid #000",
          borderRadius: 4,
          boxShadow: 1,
          p: 4,
        }}
      >
        <Typography id="modal-agregar-participante" variant="h4" mb={1} sx={{ fontWeight: "bold" }}>
          <PersonOutlinedIcon sx={{ mr: 1 }} />
          Agregar participante
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {convocatoriaTitle}
        </Typography>
        <Typography id="modal-agregar-participante-description" variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {[convocatoria?.fecha, convocatoria?.comunidad].filter(Boolean).join(" · ")}
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          El participante quedará en espera de confirmación. No será aceptado automáticamente.
        </Alert>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Autocomplete
          multiple
          value={participantesSeleccionados}
          onChange={handleSelectionChange}
          inputValue={inputParticipante}
          onInputChange={(event, newInputValue) => setInputParticipante(newInputValue)}
          options={voluntariosPool}
          loading={loadingVoluntarios}
          disabled={!canManage || loadingVoluntarios}
          disablePortal
          getOptionLabel={(option) =>
            [
              option.nombreCompleto || [option.nombres, option.apellidos].filter(Boolean).join(" "),
              getVolunteerEmail(option),
            ]
              .filter(Boolean)
              .join(" - ")
          }
          isOptionEqualToValue={(option, value) =>
            normalizeEmail(getVolunteerEmail(option)) === normalizeEmail(getVolunteerEmail(value))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Buscar voluntarios registrados"
              placeholder="Nombre, apellido o email"
              helperText="Puede seleccionar uno o varios voluntarios registrados."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingVoluntarios ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {participantesSeleccionados.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
              Seleccionados antes de enviar
            </Typography>
            <Stack spacing={0.75}>
              {participantesSeleccionados.map((voluntario) => (
                <Box
                  key={normalizeEmail(getVolunteerEmail(voluntario))}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
                    alignItems: "center",
                    gap: 1,
                    p: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" noWrap>
                    {voluntario.nombreCompleto || [voluntario.nombres, voluntario.apellidos].filter(Boolean).join(" ")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {getVolunteerEmail(voluntario)}
                  </Typography>
                  <Tooltip title="Quitar de la selección">
                    <IconButton size="small" onClick={() => handleRemoveVolunteer(voluntario)}>
                      <CloseOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={false} disabled />}
            label="Agregar participante externo / fuera de la organización"
          />
          <TextField fullWidth label="Email externo" value="" disabled sx={{ mt: 1 }} />
          <Alert severity="warning" sx={{ mt: 1 }}>
            Disponible en una próxima fase. Este participante no podrá confirmar desde la app si no tiene usuario registrado.
          </Alert>
        </Box>

        <Divider sx={{ mt: 3 }} />
        <Grid container sx={{ mt: 2 }} spacing={1} justifyContent="flex-end">
          <Grid item>
            <Button variant="contained" color="error" onClick={handleClose} disabled={saving}>
              Cancelar
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              sx={{ bgcolor: "teal", color: "white" }}
              onClick={handleSave}
              disabled={!canManage || saving || participantesSeleccionados.length === 0}
            >
              {saving ? "Guardando..." : "Agregar"}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

AgregarParticipantesConvocatoriaModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  convocatoria: PropTypes.object,
  onSuccess: PropTypes.func,
  allowedRoles: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

export default AgregarParticipantesConvocatoriaModal;
